// src/pages/PatientProfile/components/ActionButtons.jsx
import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import CreateCaseModal from './CreateCaseModal';
import { supabase } from '../../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { patientAllowsNewCase } from '../../../lib/patientStatus';
import { isAdminAssistantRole } from '../../../lib/dentistSubmissionWizard';

const ActionButtons = ({ 
  onCreateCase, 
  onEditProfile, 
  onSendMessage,
  patient,
  currentUserId 
}) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [deliveredCase, setDeliveredCase] = useState(null);
  const [showCommanderModal, setShowCommanderModal] = useState(false);
  const [isCommanderLoading, setIsCommanderLoading] = useState(false);

  useEffect(() => {
    if (currentUserId) {
      fetchUserProfile();
      fetchUserRole();
    }
  }, [currentUserId]);

  useEffect(() => {
    if (patient?.id) {
      fetchDeliveredCase();
    }
  }, [patient?.id]);

  const fetchUserProfile = async () => {
    try {
      setLoadingProfile(true);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, package_id, case_number, current_case_number')
        .eq('id', currentUserId)
        .single();

      if (error) throw error;
      setUserProfile(data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchUserRole = async () => {
    try {
      const { data: roleAssignment, error } = await supabase
        .from('user_role_assignments')
        .select(`
          id,
          is_active,
          role:user_roles (
            role_type,
            role_name
          )
        `)
        .eq('user_id', currentUserId)
        .eq('is_active', true)
        .order('assigned_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching user role:', error);
      }

      setCurrentUserRole(roleAssignment?.role?.role_type || null);
    } catch (err) {
      console.error('Unexpected error fetching user role:', err);
    }
  };

  const fetchDeliveredCase = async () => {
    if (!patient?.id) return;
    const { data, error } = await supabase
      .from('cases')
      .select('id, case_number, status')
      .eq('patient_id', patient.id)
      .eq('current_phase', 'shipping')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!error && data) setDeliveredCase(data);
    else setDeliveredCase(null);
  };

  const handleOpenModal = () => {
  

    // Tout est OK, ouvrir le modal de création
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Rafraîchir le profil après création pour mettre à jour le compteur
    if (!isAdminAssistantRole(currentUserRole)) {
      fetchUserProfile();
    }
  };

  const handleCommanderLaSuite = async () => {
    if (!deliveredCase?.id) return;
    setIsCommanderLoading(true);
    try {
      const now = new Date().toISOString();

      // Update case status → in_progress
      const { error: caseError } = await supabase
        .from('cases')
        .update({ status: 'in_progress', current_phase: 'production', updated_at: now })
        .eq('id', deliveredCase.id);
      if (caseError) throw caseError;

      // Activate group=2 / phase_order=1 → in_progress
      const { error: phaseError } = await supabase
        .from('case_timeline_phases')
        .update({ status: 'in_progress', start_date: now, updated_at: now })
        .eq('case_id', deliveredCase.id)
        .eq('group', '2')
        .eq('phase_order', 1);
      if (phaseError) throw phaseError;

      toast.success(`Suite commandée pour le cas ${deliveredCase.case_number}`);
      setShowCommanderModal(false);
      setDeliveredCase(null);
      navigate(`/case-detail/${deliveredCase.id}`);
    } catch (err) {
      console.error('Error commanding suite:', err);
      toast.error('Erreur: ' + err.message);
    } finally {
      setIsCommanderLoading(false);
    }
  };

  return (
    <>
      <div className="bg-card rounded-lg border border-border p-6 shadow-card">
        <h2 className="text-lg font-semibold text-foreground mb-4">Actions Rapides</h2>
        
        {/* Affichage du compteur de cas - seulement pour non-admin */}
 


        <div className="space-y-3">
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            fullWidth
            onClick={handleOpenModal}
            disabled={loadingProfile || !patientAllowsNewCase(patient)}
            title={
              !patientAllowsNewCase(patient)
                ? 'Patient inactif : réactivez le dossier pour créer une conception.'
                : undefined
            }
          >
            {loadingProfile ? 'Chargement...' : 'Créer une Nouvelle Conception'}
          </Button>

          <Button
            variant="outline"
            iconName="Edit"
            iconPosition="left"
            fullWidth
            onClick={onEditProfile}
          >
            Modifier le Profil
          </Button>

          {/* Commander la suite — admin only, when patient has a delivered case */}
       
        </div>
      </div>

      {/* Modal de création de cas */}
      <CreateCaseModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        patient={patient}
        currentUserId={currentUserId}
      />

      {/* Modal d'avertissement package */}
      {showPackageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-md animate-slide-in">
            <div className="p-6">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-warning/10 mx-auto mb-4">
                <Icon name="AlertTriangle" size={32} className="text-warning" />
              </div>
              
              <h2 className="text-xl font-bold text-foreground text-center mb-2">
                {!userProfile?.package_id 
                  ? 'Package requis' 
                  : 'Quota de cas épuisé'}
              </h2>
              
              <p className="text-muted-foreground text-center mb-6">
                {!userProfile?.package_id 
                  ? 'Vous devez sélectionner un package avant de créer une nouvelle conception.' 
                  : `Vous avez utilisé tous vos cas disponibles (${userProfile?.current_case_number || 0} / ${userProfile?.case_number || 0}). Veuillez sélectionner un nouveau package pour continuer.`}
              </p>

              {userProfile?.package_id && (
                <div className="bg-muted/30 border border-border rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Cas restants</span>
                    <span className="text-2xl font-bold text-foreground">
                      {userProfile?.current_case_number || 0}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 mt-2">
                    <div 
                      className="bg-warning h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${Math.max(0, ((parseInt(userProfile?.current_case_number) || 0) / (parseInt(userProfile?.case_number) || 1)) * 100)}%` 
                      }}
                    ></div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    {userProfile?.current_case_number || 0} sur {userProfile?.case_number || 0} cas disponibles
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => setShowPackageModal(false)}
                >
                  Annuler
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  iconName="Package"
                  iconPosition="left"
                  onClick={() => {
                    setShowPackageModal(false);
                    navigate('/pack-user');
                  }}
                >
                  Voir les packages
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Commander la suite */}
      {showCommanderModal && deliveredCase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-md animate-slide-in">
            <div className="p-6">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mx-auto mb-4">
                <Icon name="RefreshCw" size={32} className="text-primary" />
              </div>

              <h2 className="text-xl font-bold text-foreground text-center mb-2">
                Commander la suite
              </h2>

              <p className="text-muted-foreground text-center mb-2">
                Voulez-vous relancer le suivi du cas{' '}
                <span className="font-semibold text-foreground">{deliveredCase.case_number}</span> ?
              </p>
              <p className="text-xs text-muted-foreground text-center mb-6">
                Le statut passera à <strong>En cours</strong> et la phase de suivi (Groupe 2 — Phase 1) sera activée.
              </p>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => setShowCommanderModal(false)}
                  disabled={isCommanderLoading}
                >
                  Annuler
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  iconName={isCommanderLoading ? 'Loader' : 'RefreshCw'}
                  iconPosition="left"
                  onClick={handleCommanderLaSuite}
                  disabled={isCommanderLoading}
                >
                  {isCommanderLoading ? 'En cours...' : 'Commander la suite'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ActionButtons;